import React, { useEffect, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
} from "@mui/material";
import StepOne from "./customWindowsSteps/stepOne";
import StepTwo from "./customWindowsSteps/stepTwo";
import StepThree from "./customWindowsSteps/stepThree";
import StepFour from "./customWindowsSteps/stepFour";
import { VirtualProductQuoteRequest } from "../../models/virtualProduct/types";
import dataService from "../../services/dataService";
import { useNavigate } from "react-router-dom";
import StepConfirmationModal from "./stepConfirmationModal";
import { ERROR_MESSAGE } from "@/app/constants/constants";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store/store";
import CommonHelper from "@/app/helpers/CommonHelper";

const steps = [
  "Basic Info & Job Details",
  "Window Preferences",
  "Contractor Specifications",
  "Submitted",
];

const CustomWindowsQuote: React.FC = () => {
  const [productId] = useState(() => {
    return Number(
      new URLSearchParams(window.location.search).get("productId") || 0
    );
  });

  const isAuthenticated = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );

  const brandLabelMap: Record<string, string> = {
    harvey: "Harvey Windows + Doors",
    andersen: "Andersen",
    simonton: "Simonton",
    miwindows: "MI Windows & Doors",
    default: "Other",
  };

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<VirtualProductQuoteRequest>({
    ContactName: "",
    PhoneNumber: "",
    EmailAddress: "",
    CompanyName: "",
    JobLocation: "",
    JobDescription: "",
    Branch: "",
    PreferredMethodofContact: "Phone",
    ApplicationType: "Replacement",
    MinDateNeedTime: "",
    SpecialRequirements: "",
    JobName: "",
    IsNavigatedFromProduct: true,
    WindowModels: [],
    Screen: "Full Screen",
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const loadQuoteData = async () => {
      const response = await dataService.getCustomWindowsQuoteRequest(
        productId
      );
      if (response.data.success && response.data.data) {
        const data = response.data.data;

        const brandKey =
          data.brand?.toLowerCase().replace(/\s+/g, "") ?? "harvey";
        const resolvedBrandLabel = brandLabelMap[brandKey];

        setForm((prev) => ({
          ...prev,
          Name: data.name ?? "",
          ContactName: data.contactName ?? "",
          EmailAddress: data.emailAddress ?? "",
          PhoneNumber: CommonHelper.formatPhone334(data.phoneNumber) ?? "",
          CompanyName: data.companyName ?? "",
          Branch: data.branch ?? "",
          Brand: resolvedBrandLabel ?? "",
          OtherBrand: data.otherBrand ?? "",
          MinDateNeedTime: data.minDateNeedTime?.split("T")[0] ?? "",
          Materials: data.materials ?? [],
          ProductLines: data.productLines ?? [],
          ProductSeries: data.productSeries ?? [],
          WindowTypes: data.windowTypes ?? [],
          IsNavigatedFromProduct: data.isNavigatedFromProduct ?? false,
        }));
      }
    };

    loadQuoteData();
  }, [productId]);

  const handleSkip = () => setShowConfirmModal(true);

  const handleClose = () => setShowConfirmModal(false);

  const handleContinue = () => {
    setShowConfirmModal(false);
    handleNext();
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);

    const formData = new FormData();

    formData.append("JobName", form.JobName ?? "");
    formData.append("ContactName", form.ContactName ?? "");
    formData.append("PhoneNumber", form.PhoneNumber ?? "");
    formData.append("EmailAddress", form.EmailAddress ?? "");
    formData.append("Branch", form.Branch ?? "");
    formData.append(
      "IsNavigatedFromProduct",
      String(form.IsNavigatedFromProduct ?? false)
    );

    if (form.MinDateNeedTime) {
      formData.append(
        "MinDateNeedTime",
        new Date(form.MinDateNeedTime).toISOString()
      );
    }

    const optionalFields: (keyof typeof form)[] = [
      "Brand",
      "CompanyName",
      "ApplicationType",
      "PreferredMethodofContact",
      "JobLocation",
      "JobDescription",
      "SpecialRequirements",
      "OtherBrand",
      "OtherWindowType",
      "ProjectAddress",
      "Screen",
      "NumberOfWindows",
    ];

    optionalFields.forEach((field) => {
      if (form[field]) {
        formData.append(field, String(form[field]));
      }
    });

    if (typeof form.Grid === "boolean") {
      formData.append("Grid", String(form.Grid));
    }
    if (typeof form.Screens === "boolean") {
      formData.append("Screens", String(form.Screens));
    }

    const jsonFields: (keyof typeof form)[] = [
      "Materials",
      "ProductLines",
      "ProductSeries",
      "WindowCollection",
      "WindowTypes",
    ];

    jsonFields.forEach((field) => {
      if (form[field]) {
        formData.append(field, JSON.stringify(form[field]));
      }
    });

    const windowModels = form.WindowModels ?? [];
    if (windowModels.length === 0) {
      formData.append("WindowModels", JSON.stringify([]));
    } else {
      windowModels.forEach((model, index) => {
        formData.append(
          `WindowModels[${index}].WindowType`,
          model.WindowType ?? ""
        );
        formData.append(
          `WindowModels[${index}].Preferences`,
          model.Preferences ?? ""
        );
        formData.append(
          `WindowModels[${index}].Qty`,
          String(model.Quantity || 0)
        );
        formData.append(
          `WindowModels[${index}].Width`,
          String(model.Width || 0)
        );
        formData.append(
          `WindowModels[${index}].Height`,
          String(model.Height || 0)
        );
        formData.append(
          `WindowModels[${index}].SizeType`,
          model.SizeType ?? ""
        );
      });
    }

    form.Files?.forEach((file) => {
      formData.append("Files", file);
    });

    try {
      const response = await dataService.sendCustomWindowsQuoteRequest(
        productId,
        formData
      );
      setActiveStep(3);
      setSubmitMessage(
        response?.data?.message ?? "Form submitted successfully."
      );
      if (productId > 0 && isAuthenticated) {
        await dataService.sendWindowsPatioDoorsSSE(productId, {
          Branch: form.Branch ?? "",
          Brand: form.Brand ?? "",
          FrameType: form.ApplicationType ?? "",
          Materials: form.Materials ?? [],
        });
      }
    } catch (error) {
      console.error(error);
      setSubmitMessage(ERROR_MESSAGE);
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);

  const handleBack = () => {
    if (activeStep == 0) {
      if (productId > 0) navigate(`/product?id=${productId}`);
      else navigate("/gallery?searchWindows=true");
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const stepProps = {
    form,
    setForm,
    onNext: handleNext,
    onBack: handleBack,
    onSubmit: handleSubmit,
    setSubmitMessage,
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <StepOne {...stepProps} />;
      case 1:
        return <StepTwo {...stepProps} />;
      case 2:
        return <StepThree {...stepProps} />;
      case 3:
        return <StepFour message={submitMessage} />;
      default:
        return <></>;
    }
  };

  return (
    <Box className="quote">
      <div className="text-center page-margin">
        <div id="ph-title" className="page-title">
          <h1>Windows</h1>
        </div>
        <Typography
          variant="body1"
          textAlign="center"
          mb={5}
          mt={5}
          ml={23}
          mr={23}
          fontFamily={'"Montserrat", Verdana, sans-serif'}
        >
          Whatever the architectural, performance or budgetary needs, Lansing is
          the clear choice for new construction or replacement windows and patio
          doors. We offer several of the best window brands in the industry and
          are committed to providing superior service when quoting and ordering
          windows for your next job.
        </Typography>
      </div>

      <Box maxWidth="1100px" mx="auto" px={2} py={2}>
        {activeStep !== 4 && (
          <Typography
            textAlign="center"
            mb={5}
            fontWeight={600}
            fontFamily={'"Montserrat", Verdana, sans-serif'}
          >
            Need a window quote? Complete the form below and we’ll reach out
            with pricing on your project.
          </Typography>
        )}

        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label} className="step">
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
          {activeStep >= 0 && activeStep < steps.length - 1 && (
            <>
              <Button onClick={handleBack} className="back-button">
                Back
              </Button>
              {activeStep > 0 && (
                <Button onClick={handleSkip} className="skip-button">
                  Skip This Step
                </Button>
              )}
            </>
          )}
        </Box>

        {renderStep()}
      </Box>

      <StepConfirmationModal
        currentStep={activeStep}
        show={showConfirmModal}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onContinue={handleContinue}
      />
    </Box>
  );
};

export default CustomWindowsQuote;
